import { i18n } from "../../i18n";

/**
 * Turn whatever an image upload rejected with into one short, readable sentence.
 *
 * graphql-request throws a `ClientError` whose own `.message` is the server's
 * message *followed by a JSON dump* of the response, the query and the
 * variables. Alerting that object is what put a wall of GraphQL internals in
 * front of planner users who picked a file over the 15 MB cap. The readable
 * half is `response.errors[0].message` — the `GraphQL::ExecutionError` the
 * resolver raised deliberately, which is already written for a reader.
 *
 * Anything else (a failed PUT to object storage, a dropped connection, a 500
 * with no GraphQL error body) carries nothing worth showing, so it falls back
 * to a localized generic rather than leaking a status line.
 */
export function imageUploadErrorMessage(error: unknown, filename?: string): string {
  const message = graphqlErrorMessage(error) || i18n.global.t('utils.image_upload_error.generic');

  return filename ? `${filename}: ${message}` : message;
}

/** The first non-empty `errors[].message` of a GraphQL response, if there is one. */
function graphqlErrorMessage(error: unknown): string | null {
  const errors = (error as { response?: { errors?: unknown } })?.response?.errors;
  if (!Array.isArray(errors)) return null;

  for (const entry of errors) {
    const message = (entry as { message?: unknown })?.message;
    if (typeof message === 'string' && message.trim()) return message.trim();
  }

  return null;
}
